import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    ImageList,
    ImageListItem,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../../../firebase/firebase';
import { GalleryItem } from '../../../types';
import { getCachedAssetUrl } from '../../../utils/assetCache';

interface GallerySectionProps {
    gallery: GalleryItem[];
}

type GalleryImage = GalleryItem & { imageURL?: string | null };

const COLLAPSED_VISIBLE_IMAGES = 6;

const GallerySection: React.FC<GallerySectionProps> = ({ gallery }) => {
    const [images, setImages] = useState<GalleryImage[]>([]);
    const [expanded, setExpanded] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        setImages(gallery.map((item) => ({ ...item })));
        const controller = new AbortController();

        const loadImage = async (item: GalleryItem, index: number) => {
            try {
                const url = await getCachedAssetUrl(item.imagePath, async () => {
                    const downloadUrl = await getDownloadURL(ref(storage, item.imagePath));
                    return downloadUrl;
                });

                if (!url || controller.signal.aborted) {
                    return;
                }

                setImages((prev) => {
                    const next = [...prev];
                    if (!next[index]) {
                        return prev;
                    }
                    next[index] = { ...next[index], imageURL: url };
                    return next;
                });
            } catch (error) {
                if (!controller.signal.aborted) {
                    console.error('Failed to load gallery image:', item.imagePath, error);
                }
            }
        };

        gallery.forEach((item, index) => {
            if (item.imageURL) {
                return;
            }
            loadImage(item, index);
        });

        return () => {
            controller.abort();
        };
    }, [gallery]);

    useEffect(() => {
        setExpanded(false);
    }, [gallery.length]);

    const hasMoreImages = images.length > COLLAPSED_VISIBLE_IMAGES;
    const clampGallery = hasMoreImages && !expanded;
    const collapsedMaxHeight = isMobile ? 600 : 1000;

    const toggleExpanded = () => setExpanded((prev) => !prev);

    return (
        <Box sx={{ px: 2, mb: 2 }}>
            <Typography
                variant="h4"
                align="center"
                gutterBottom
                fontWeight={600}
            >
                Our Gallery
            </Typography>
            <Box sx={{ position: 'relative', mt: 3 }}>
                <Box
                    sx={{
                        overflow: 'hidden',
                        maxHeight: clampGallery ? collapsedMaxHeight : 'none',
                        transition: 'max-height 0.45s ease',
                    }}
                >
                    <ImageList
                        variant="masonry"
                        cols={isMobile ? 2 : 3}
                        gap={16}
                    >
                        {images.map((item) => {
                            const hasCaption = Boolean(item.title?.trim() || item.description?.trim());
                            return (
                                <ImageListItem
                                    key={item.imagePath}
                                    sx={{
                                        position: 'relative',
                                        overflow: 'hidden',
                                        borderRadius: 2,
                                        cursor: hasCaption ? 'pointer' : 'default',
                                        '&:hover .caption': hasCaption
                                            ? {
                                                opacity: 1,
                                                transform: 'translateY(0)',
                                            }
                                            : undefined,
                                    }}
                                >
                                    {item.imageURL ? (
                                        <img
                                            src={item.imageURL}
                                            alt={item.title || 'Gallery image'}
                                            loading="lazy"
                                            style={{ borderRadius: 8, width: '100%', display: 'block' }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                pt: '75%',
                                                borderRadius: 2,
                                                backgroundColor: (t) =>
                                                    t.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : t.palette.grey[200],
                                            }}
                                        />
                                    )}
                                    {hasCaption && (
                                        <Box
                                            className="caption"
                                            sx={{
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                bgcolor: 'rgba(0,0,0,0.6)',
                                                color: 'white',
                                                px: 2,
                                                py: 1,
                                                opacity: 0,
                                                transform: 'translateY(20px)',
                                                transition: 'all 0.3s ease',
                                            }}
                                        >
                                            {item.title && <Box fontWeight="bold">{item.title}</Box>}
                                            {item.description && (
                                                <Box fontSize="0.875rem">{item.description}</Box>
                                            )}
                                        </Box>
                                    )}
                                </ImageListItem>
                            );
                        })}
                    </ImageList>
                </Box>
                {clampGallery && (
                    <Box
                        sx={(t) => ({
                            pointerEvents: 'none',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 160,
                            background: `linear-gradient(0deg, ${t.palette.background.default} 0%, rgba(${t.palette.mode === 'dark' ? '18,18,18' : '255,255,255'},0.85) 40%, rgba(${t.palette.mode === 'dark' ? '18,18,18' : '255,255,255'},0))`,
                            display: 'flex',
                            alignItems: 'flex-end',
                            justifyContent: 'center',
                        })}
                    >
                        <Box pb={2} sx={{ pointerEvents: 'auto' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={toggleExpanded}
                                endIcon={<ExpandMoreIcon />}
                                sx={{
                                    borderRadius: 999,
                                    px: 3,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                }}
                            >
                                Show More Photos
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
            {expanded && hasMoreImages && (
                <Box mt={2} display="flex" justifyContent="center">
                    <Button
                        variant="text"
                        color="inherit"
                        onClick={toggleExpanded}
                        endIcon={<ExpandLessIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Show Fewer Photos
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default GallerySection;
